/**
 * Email Account Status Badge Component
 *
 * Displays a colored badge indicating the status of an email account.
 * Uses semantic colors to communicate status at a glance.
 */

import { Badge, BadgeProps } from "@mantine/core";
import type { EmailAccountStatus } from "../../schema/email-account.types";

/**
 * Status configuration mapping
 */
const STATUS_CONFIG: Record<
  EmailAccountStatus,
  {
    label: string;
    color: string;
    variant?: BadgeProps["variant"];
  }
> = {
  pending: {
    label: "Pendente",
    color: "yellow",
    variant: "light"
  },
  connected: {
    label: "Conectado",
    color: "green",
    variant: "light"
  },
  expired: {
    label: "Expirado",
    color: "orange",
    variant: "light"
  },
  error: {
    label: "Erro",
    color: "red",
    variant: "light"
  }
};

/**
 * Props for EmailAccountStatusBadge
 */
export interface EmailAccountStatusBadgeProperties {
  /** Account status */
  status: EmailAccountStatus;
  /** Optional badge size */
  size?: BadgeProps["size"];
  /** Optional badge radius */
  radius?: BadgeProps["radius"];
}

/**
 * EmailAccountStatusBadge Component
 * @param root0
 * @param root0.status
 * @param root0.size
 * @param root0.radius
 * @example
 * <EmailAccountStatusBadge status="connected" />
 * <EmailAccountStatusBadge status="expired" size="lg" />
 */
export function EmailAccountStatusBadge({ status, size = "sm", radius = "sm" }: EmailAccountStatusBadgeProperties) {
  const config = STATUS_CONFIG[status];

  return (
    <Badge color={config.color} variant={config.variant} size={size} radius={radius}>
      {config.label}
    </Badge>
  );
}

EmailAccountStatusBadge.displayName = "EmailAccountStatusBadge";
